import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import { generatePalette } from 'emoji-palette';
import {
  createContext,
  forwardRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useSlottedContext, type ContextValue } from 'react-aria-components';

import { roundToPrecision } from '@infinex-private/ts-utils/number';
import { WALLET_ICONS } from '@infinex/ui-assets/wallet-icons';
import type { DecorativeColorKey } from '@infinex/ui-constants';
import { decorativeColors } from '@infinex/ui-constants';
import { HotWalletIcon, FileIcon } from '@infinex/ui-icons';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, styleVars, useContextProps } from '@infinex/ui-styles';
import { formatInitials } from '@infinex/ui-utils/format';

import {
  INITIALS_FONT,
  INITIALS_PROPORTION,
  MAX_INITIALS_WIDTH,
  SIZES,
  SIZE_CLASSES,
} from './constants';

// Context ---------------------------------------------------------------------

type WalletAvatarContextValue = Partial<WalletAvatarProps> & {
  /**
   * Wrap the element with some custom behaviour, within a slotted component.
   */
  render?: (element: ReactNode) => ReactNode;
};

export const WalletAvatarContext = createContext<
  ContextValue<WalletAvatarContextValue, HTMLDivElement>
>({});

// Types -----------------------------------------------------------------------

type ContactType = { type: 'contact'; color: AvatarColorKey; name: string };
type ImportIconType = {
  type: 'import';
  color: AvatarColorKey;
  icon: AvatarIconKey;
};
type ImportEmojiType = { type: 'import'; emoji: string };
type UnknownType = { type: 'unknown'; isSmartContract?: boolean };
type TypeDiscriminant =
  | ContactType
  | ImportIconType
  | ImportEmojiType
  | UnknownType;

export type AvatarIconKey = keyof typeof WALLET_ICONS;
export type AvatarColorKey = DecorativeColorKey;
export type AvatarSizeKey = keyof typeof SIZES;

// Main ------------------------------------------------------------------------

export type WalletAvatarProps = {
  /**
   * The size of the avatar element.
   *
   * @default large
   */
  size?: AvatarSizeKey;
} & TypeDiscriminant &
  DOMProps &
  UnsafeStyleProps;

/**
 * A symbolic image that serves as a graphic representation of a wallet,
 * address, or smart contract.
 */
export const WalletAvatar = forwardRef<HTMLDivElement, WalletAvatarProps>(
  function WalletAvatar(props, ref) {
    [props, ref] = useContextProps(props, ref, WalletAvatarContext);
    const ctx = useSlottedContext(WalletAvatarContext);
    const { size = 'large', type, className, style } = props;

    useFont(props);

    const element = (
      <div
        {...filterDOMProps(props)}
        ref={ref}
        aria-hidden // always decorative
        className={cn(
          'grid shrink-0 place-content-center rounded-full select-none',
          'text-secondary bg-current/12',
          SIZE_CLASSES[size].element,
          {
            'font-bold text-(--fg)': type === 'contact' || type === 'import',
          },
          className
        )}
        style={styleVars({
          ...style,
          // order matters: emoji color takes precedence over icon color
          '--fg': getEmojiColor(props) || getOklch(props),
        })}
      >
        {getContent(props)}
      </div>
    );

    if (ctx?.render) {
      return ctx.render(element);
    }

    return element;
  }
);

// Styled components -----------------------------------------------------------

/** Renders contact initials and applies size-aware scaling. */
function Initials(props: WalletAvatarProps & ContactType) {
  const { name, size = 'large' } = props;

  const initials = useMemo(() => formatInitials(name), [name]);
  const scale = useMemo(
    () => getInitialsScale(initials, size),
    [initials, size]
  );

  return (
    <span
      className={cn(
        SIZE_CLASSES[size].font,
        // tween the scale transformation to avoid awkward jumps on slow devices
        'font-round transition-transform'
      )}
      style={{ scale }}
    >
      {initials}
    </span>
  );
}

// Typography ------------------------------------------------------------------

let fontLoadPromise: Promise<void> | null = null;
let fontLoadAttempts = 0; // limit attempts to avoid infinite loops

/** Load the "Open Runde" font on demand. */
// Font is only used by this component for contact initials, so load on demand.
function useFont(props: WalletAvatarProps) {
  useEffect(() => {
    if (props.type === 'contact' && !fontLoadPromise) {
      fontLoadPromise = loadFont();
    }
  }, [props.type]);
}

/** Load the "Open Runde" font. */
async function loadFont() {
  if (!('fonts' in document) || fontLoadAttempts >= 3) return;
  fontLoadAttempts++;

  try {
    const { name, options, url } = INITIALS_FONT;
    const font = new FontFace(name, `url(${await url()})`, options);
    await font.load();
    document.fonts.add(font);
  } catch (error) {
    console.error(`Failed to load font "${INITIALS_FONT.name}"`, error);
    fontLoadPromise = null; // reset to allow retry on next render
  }
}

/**
 * Returns the scale factor (0-1) of the initials, based on the width of the
 * text and the maximum width of the initials for a given size.
 */
function getInitialsScale(initials: string, size: AvatarSizeKey = 'large') {
  const maxWidth = MAX_INITIALS_WIDTH[size];
  const width = getTextWidth(initials, size);

  if (width > maxWidth) {
    return roundToPrecision(maxWidth / width, 2);
  }
}

const INITIALS_WIDTH_CACHE = new Map<string, number>();

/**
 * Measure the width of a text string, using canvas to avoid layout thrashing.
 * Cache the results to avoid re-measuring the same text/size combination.
 */
function getTextWidth(text: string, size: AvatarSizeKey) {
  const key = text + size;
  const cached = INITIALS_WIDTH_CACHE.get(key);
  if (cached) return cached;

  const fontSize = SIZES[size].font;
  const defaultWidth = fontSize * text.length * INITIALS_PROPORTION;

  if (typeof document === 'undefined') {
    return defaultWidth;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return defaultWidth;

  ctx.font = `bold ${fontSize}px ${INITIALS_FONT.name}, sans-serif`;
  const { width } = ctx.measureText(text);
  INITIALS_WIDTH_CACHE.set(key, width);

  return width;
}

// Helpers ---------------------------------------------------------------------

/**
 * Returns the content of the `WalletAvatar`, based on the `type` prop.
 */
function getContent(props: WalletAvatarProps): ReactNode {
  const { size = 'large' } = props;
  const iconClassname = SIZE_CLASSES[size].icon;

  switch (props.type) {
    case 'contact':
      return <Initials {...props} />;
    case 'import': {
      if (hasEmoji(props))
        return <span className={SIZE_CLASSES[size].emoji}>{props.emoji}</span>;

      const Icon = WALLET_ICONS[props.icon];
      return <Icon className={iconClassname} />;
    }
    case 'unknown':
      return props.isSmartContract ? (
        <FileIcon className={iconClassname} />
      ) : (
        <HotWalletIcon className={iconClassname} />
      );
    default:
      return null;
  }
}

const EMOJI_COLOR_CACHE = new Map<string, string>();

/**
 * Determines the dominant (most luminous) color for a given emoji.
 */
function getEmojiColor(props: WalletAvatarProps) {
  if (hasEmoji(props)) {
    return getDominantEmojiColor(props.emoji);
  }
}

function getDominantEmojiColor(emoji: string) {
  const cached = EMOJI_COLOR_CACHE.get(emoji);
  if (cached) return cached;

  const dominantColor = normalisedOklch(generatePalette(emoji)[0]!);
  EMOJI_COLOR_CACHE.set(emoji, dominantColor);

  return dominantColor;
}

/**
 * The foreground color a `WalletAvatar` renders for the given avatar value —
 * for surfaces that tint adjacent decoration (e.g. a page gradient) to match
 * the avatar's theme.
 */
export function getWalletAvatarColor(
  avatar: { color: AvatarColorKey } | { emoji: string }
): string {
  return 'emoji' in avatar
    ? getDominantEmojiColor(avatar.emoji)
    : normalisedOklch(decorativeColors[avatar.color]);
}

/**
 * Converts the color to `oklch` for perceptual uniformity.
 */
function getOklch(props: WalletAvatarProps) {
  if (hasColor(props)) {
    return normalisedOklch(decorativeColors[props.color]);
  }
}

/** Normalise the lightness and chroma of a given color. */
function normalisedOklch(color: string) {
  return `oklch(from ${color} 0.8 0.25 h)`;
}

function hasColor(props: WalletAvatarProps) {
  return (
    (props.type === 'contact' || props.type === 'import') && 'color' in props
  );
}

function hasEmoji(props: WalletAvatarProps) {
  return props.type === 'import' && 'emoji' in props;
}
